import { useAtomValue, useSetAtom } from 'jotai';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { TableSkeleton } from '@/lib/components';
import { Table } from '@/lib/components/Table';
import { EmptyCampaignsIcon, EmptyCampaignsNoResultIcon } from '@/lib/v2/icons/general';
import { EmptyScreenTable } from '@/lib/v2/organisms';

import { useCampaignRecurrentTableColumns } from './hooks/useCampaignRecurrentTableColumns';
import { useHandleEvents } from './hooks/useHandleEvents';

import {
  atomCampaignsRecurrentList,
  atomLoadingProcess,
  atomPagerCampaignsRecurrent,
  atomSelectedRows,
  atomTotalsProcess,
} from '@/modules/CampaignsModule/atoms/campaignRecurrentAtom';
import { EmptyTableWithoutData } from '@/modules/CampaignsModule/components/Campaigns/TableContainer/components/EmptyTableWithoutData';
import { defaultPager } from '@/modules/CampaignsModule/constants';

interface CampaignRecurrentTableProps {
  stickyHeader?: boolean;
}

const CampaignRecurrentTable = ({ stickyHeader }: CampaignRecurrentTableProps) => {
  const { t } = useTranslation();

  const totalsCampaignList = useAtomValue(atomTotalsProcess);
  const loadingProcess = useAtomValue(atomLoadingProcess);
  const recurrentMessageList = useAtomValue(atomCampaignsRecurrentList);
  const pager = useAtomValue(atomPagerCampaignsRecurrent);
  const setSelectedRows = useSetAtom(atomSelectedRows);

  const { recurrentColumns } = useCampaignRecurrentTableColumns();
  const {
    handleClickEmptyScreen,
    handleClickEmptyScreenNoResult,
    handleChangeTablePage,
    handleRowClick,
  } = useHandleEvents();

  const totalRecords = useMemo(
    () => Math.ceil(totalsCampaignList.partial / (pager?.limit || defaultPager.limit)),
    [totalsCampaignList.partial, pager?.limit]
  );

  const emptyScreen = useMemo(
    () => ({
      totalCount: totalsCampaignList.total,
      noResult: (
        <EmptyScreenTable
          actionText={t(
            'RECURRENT_MESSAGE.TABLE.EMPTY_SCREEN_TABLE_NO_RESULTS.emptyCampaignsActionText'
          )}
          description={t(
            'RECURRENT_MESSAGE.TABLE.EMPTY_SCREEN_TABLE_NO_RESULTS.emptyCampaignsDescription'
          )}
          id="noResults-campaigns"
          image={<EmptyCampaignsNoResultIcon />}
          title={t('RECURRENT_MESSAGE.TABLE.EMPTY_SCREEN_TABLE_NO_RESULTS.emptyCampaignsTitle')}
          onClickAction={handleClickEmptyScreenNoResult}
        />
      ),
      noData: (
        <EmptyScreenTable
          description={<EmptyTableWithoutData onClickAction={handleClickEmptyScreen} />}
          image={<EmptyCampaignsIcon />}
          title={t('CAMPAIGNS_MAIN.EMPTY_SCREEN_TABLE.emptyCampaignsTitle')}
        />
      ),
    }),
    [handleClickEmptyScreen, handleClickEmptyScreenNoResult, t, totalsCampaignList]
  );

  if (loadingProcess) {
    return <TableSkeleton columns={7} />;
  }

  return (
    <div className="w-full text-base text-[#364365] 2xl:text-14">
      <Table
        noNegativeMargin
        classNamePagination="px-4 mb-2"
        columns={recurrentColumns}
        data={recurrentMessageList}
        defaultSize={pager?.limit}
        emptyScreen={emptyScreen}
        gotoPageIndex={pager?.page ? pager.page - 1 : 0}
        isLoading={loadingProcess}
        lastPageViewed={pager?.page}
        setSelectedRows={setSelectedRows}
        stickyHeader={stickyHeader}
        withCheckbox={true}
        withPagination={{
          totalPages: totalRecords,
          fetchData: handleChangeTablePage,
        }}
        onRowClick={handleRowClick}
      />
    </div>
  );
};

export default CampaignRecurrentTable;
